import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { setStatus, clearSelection } from '../../store/userGenerationSlice';
import { getOperationStatus, getLicServers, getInfraServers } from '../../services/api';
import translations from '../../locales/translations';
import { useSidebar } from '../../contexts/SidebarContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import BulkGenerationTab from './BulkGenerationTab';
import SingleUserTab from './SingleUserTab';
import AddNUsersTab from './AddNUsersTab';
import OperationProgressPanel from './OperationProgressPanel';
import type { StatusColor, OperationState, OperationErrors, OperationResult } from './UserGenerationTypes';
import { EMPTY_OPERATION_ERRORS } from './UserGenerationTypes';
import { useNewGui } from '../../hooks/useNewGui';
import PageHeader from '../shared/PageHeader';

type ActiveTab = 'bulk' | 'single' | 'addn';

const UserGenerationPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();
  const { fileUploaded, selectedRows } = useSelector((state: RootState) => state.userGeneration);

  const [activeTab, setActiveTab] = useState<ActiveTab>('bulk');
  const [licenseServers, setLicenseServers] = useState<any[]>([]);
  const [infraServers, setInfraServers] = useState<any[]>([]);
  const [selectedLicenseServerId, setSelectedLicenseServerId] = useState<string>(
    () => localStorage.getItem('userGen.licenseServerId') || ''
  );

  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusColor, setStatusColor] = useState<StatusColor | null>(null);
  const [currentOperation, setCurrentOperation] = useState<OperationState | null>(null);
  const [operationErrors, setOperationErrors] = useState<OperationErrors>(EMPTY_OPERATION_ERRORS);
  const [operationResults, setOperationResults] = useState<OperationResult[]>([]);
  const [lastOperationData, setLastOperationData] = useState<any>(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const updateStatus = useCallback((msg: string, color: StatusColor) => {
    setStatusMessage(msg);
    setStatusColor(color);
  }, []);

  const resetOperation = useCallback(() => {
    setCurrentOperation(null);
    setOperationErrors(EMPTY_OPERATION_ERRORS);
    setOperationResults([]);
    setLastOperationData(null);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback((operationId: string) => {
    // HARD_TIMEOUT is the real budget (kept above the backend's own 600s cca
    // exec timeout so a slow multi-user batch is reported by the backend, not
    // cut off client-side first). MAX_POLLS defaults to a derived value so it
    // can't silently expire before HARD_TIMEOUT if only one of the two is
    // overridden - it previously defaulted to 30 polls (1 minute), which is
    // what caused batches of ~20 users to be reported as "timed out" here even
    // though they went on to succeed on the target.
    const INTERVAL = parseInt(process.env.REACT_APP_FRONTEND_POLLING_INTERVAL_SECONDS || '2') * 1000;
    const MAX_ERRORS = parseInt(process.env.REACT_APP_FRONTEND_MAX_CONSECUTIVE_ERRORS || '3');
    const HARD_TIMEOUT = parseInt(process.env.REACT_APP_HARD_TIMEOUT_SECONDS || '660') * 1000;
    const MAX_POLLS = parseInt(process.env.REACT_APP_OPERATION_MAX_POLLS || String(Math.ceil(HARD_TIMEOUT / INTERVAL) + 5));

    let polls = 0;
    let consecutiveErrors = 0;
    const startTime = Date.now();

    const poll = async () => {
      try {
        const elapsed = Date.now() - startTime;
        if (elapsed > HARD_TIMEOUT) {
          if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
          updateStatus(`Operation timed out after ${HARD_TIMEOUT / 1000}s — check status manually`, 'red');
          setCurrentOperation(prev => prev ? { ...prev, status: 'failed', message: 'Operation timed out' } : null);
          return;
        }

        polls++;
        if (polls > MAX_POLLS) {
          if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
          updateStatus('Operation timed out after max polls — please try again or contact support', 'red');
          setCurrentOperation(prev => prev ? { ...prev, status: 'failed', message: 'Operation timed out' } : null);
          return;
        }

        const res = await getOperationStatus(operationId);
        consecutiveErrors = 0;

        if (!res) {
          if (polls > 10) {
            if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
            updateStatus('Operation failed to start — please try again', 'red');
            setCurrentOperation(prev => prev ? { ...prev, status: 'failed', message: 'Operation failed to start' } : null);
          }
          return;
        }

        const lastUpdate = res.lastUpdate || (res.updates?.length ? res.updates[res.updates.length - 1] : null);

        setCurrentOperation({
          operationId, operationType: res.operationType || 'user_generation',
          status: res.status, progress: res.progress || 0, message: res.message,
          startTime: new Date(res.startTime),
          endTime: res.endTime ? new Date(res.endTime) : undefined,
        });
        if (lastUpdate?.data?.results) setOperationResults(lastUpdate.data.results);
        if (lastUpdate?.data) setLastOperationData(lastUpdate.data);

        if (res.status === 'completed' || res.status === 'failed') {
          if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
          if (res.status === 'completed') {
            updateStatus(`Operation completed successfully: ${res.message}`, 'blue');
            dispatch(setStatus(translations.en.genOprnCompleted));
            dispatch(clearSelection());
            if (lastUpdate?.data) {
              setOperationErrors(prev => ({
                ...prev,
                skipped: lastUpdate.data.duplicateUsers || 0,
                failed: lastUpdate.data.failedUsers || 0,
                duplicates: lastUpdate.data.duplicateDetails || [],
              }));
              if (lastUpdate.data.resultsCsv) setShowResultsModal(true);
            }
          } else {
            const detail = lastUpdate?.data?.error ? ` (${lastUpdate.data.error})` : '';
            updateStatus(`Operation failed: ${res.message}${detail}`, 'red');
            dispatch(setStatus('Operation Failed'));
          }
        }
      } catch {
        consecutiveErrors++;
        if (consecutiveErrors >= MAX_ERRORS) {
          if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
          updateStatus('Failed to monitor operation progress — check status manually', 'red');
          setCurrentOperation(prev => prev ? { ...prev, status: 'failed', message: 'Connection lost' } : null);
        }
      }
    };

    poll();
    pollingIntervalRef.current = setInterval(poll, INTERVAL);
  }, [dispatch, updateStatus]);

  useEffect(() => {
    getLicServers({}).then(s => setLicenseServers(s || [])).catch(() => setLicenseServers([]));
    getInfraServers().then(s => setInfraServers(s || [])).catch(() => setInfraServers([]));
  }, []);

  useEffect(() => {
    return () => { if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current); };
  }, []);

  const actionMessage = () => {
    if (activeTab === 'addn') return 'Select target system (VNC FQDN), set options, then click Generate N Users.';
    if (activeTab === 'single') return 'Fill required fields (marked *) and click Generate User.';
    if (!selectedLicenseServerId) return 'Select a target license server to continue.';
    if (!fileUploaded) return 'Upload an Excel file to start bulk generation.';
    if (selectedRows.length === 0) return 'Select at least one user to generate.';
    return 'Click Generate Users to start the operation.';
  };

  const TAB_CLASS = (tab: ActiveTab) => useNew
    ? `px-3 md:px-4 py-2 -mb-px text-sm font-medium rounded-t transition ${
        activeTab === tab
          ? 'text-accent bg-accent/5 border-b-2 border-accent'
          : 'text-ink-secondary hover:text-ink-primary hover:bg-surface-raised border-b-2 border-transparent'
      }`
    : `px-3 md:px-4 py-2 -mb-px text-sm font-medium rounded-md transition ${
        activeTab === tab
          ? 'text-blue-700 bg-blue-50 border-b-2 border-blue-600'
          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 border-b-2 border-transparent'
      }`;

  return (
    <div className={`flex-1 p-6 bg-gray-50 transition-all duration-300 font-sans ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
      {useNew ? (
        <PageHeader title="User Generation" purpose="Create infra user accounts in bulk from Excel, or one at a time." />
      ) : (
        <h1 className="text-3xl font-semibold text-gray-800 mb-4">
          {translations.en.userGenerationPageTitle}
        </h1>
      )}

      {/* License server selector — common to bulk and single tabs */}
      <div className={useNew ? "bg-accent/5 border border-accent/20 rounded-lg p-4 mb-4" : "bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4 shadow-sm"}>
        <label className={useNew ? "block text-sm font-medium text-ink-primary mb-2" : "block text-sm font-medium text-gray-700 mb-2"}>Target License Server *</label>
        <select
          value={selectedLicenseServerId}
          onChange={e => { setSelectedLicenseServerId(e.target.value); localStorage.setItem('userGen.licenseServerId', e.target.value); }}
          className={useNew
            ? "w-full px-3 py-2 border border-border-strong rounded bg-surface text-ink-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
            : "w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500"
          }
        >
          <option value="">Select Target License Server *</option>
          {licenseServers.map(s => (
            <option key={s.id} value={s.id}>{s.serverFqdn}:{s.servicePort} ({s.serverType})</option>
          ))}
        </select>
        {licenseServers.length === 0 && <p className={useNew ? "text-sm text-warning mt-1" : "text-sm text-orange-600 mt-1"}>Loading license servers...</p>}
      </div>

      <div className={useNew ? "mb-4 text-sm text-ink-secondary" : "mb-4 text-sm text-gray-600"}>{actionMessage()}</div>

      {/* Tab bar */}
      <div className={useNew ? "mb-4 border-b border-border-subtle" : "mb-4 border-b border-gray-200"}>
        <div className="flex space-x-2 md:space-x-6">
          <button className={TAB_CLASS('bulk')} onClick={() => setActiveTab('bulk')}>Bulk (Excel)</button>
          <button className={TAB_CLASS('single')} onClick={() => setActiveTab('single')}>Single User</button>
          <button className={TAB_CLASS('addn')} onClick={() => setActiveTab('addn')}>Add N Users</button>
        </div>
      </div>

      {activeTab === 'bulk' && (
        <BulkGenerationTab
          selectedLicenseServerId={selectedLicenseServerId}
          currentOperation={currentOperation}
          updateStatus={updateStatus}
          startPolling={startPolling}
          resetOperation={resetOperation}
        />
      )}
      {activeTab === 'single' && (
        <SingleUserTab
          selectedLicenseServerId={selectedLicenseServerId}
          infraServers={infraServers}
          startPolling={startPolling}
          resetOperation={resetOperation}
          updateStatus={updateStatus}
        />
      )}
      {activeTab === 'addn' && (
        <AddNUsersTab infraServers={infraServers} updateStatus={updateStatus} />
      )}

      <OperationProgressPanel
        statusMessage={statusMessage}
        statusColor={statusColor}
        currentOperation={currentOperation}
        operationErrors={operationErrors}
        setOperationErrors={setOperationErrors}
        operationResults={operationResults}
        lastOperationData={lastOperationData}
        showResultsModal={showResultsModal}
        setShowResultsModal={setShowResultsModal}
        updateStatus={updateStatus}
      />
    </div>
  );
};

export default UserGenerationPage;
